import React, { createContext, useContext, useState, useRef, ReactNode } from 'react';

interface AnswerSectionOverlayContextValue {
  /** Container element where AnswerSection should render when in overlay mode */
  overlayContainer: HTMLElement | null;
  /** Set the overlay container element */
  setOverlayContainer: (container: HTMLElement | null) => void;
  /** Whether overlay mode is currently active */
  isOverlayActive: boolean;
  /** Activate overlay mode */
  activateOverlay: () => void;
  /** Deactivate overlay mode (collapse back to chat interface) */
  deactivateOverlay: () => void;
  /** Thread ID for the current overlay conversation */
  overlayThreadId: string | null;
  /** Set the thread ID for overlay mode */
  setOverlayThreadId: (threadId: string | null) => void;
}

const AnswerSectionOverlayContext = createContext<AnswerSectionOverlayContextValue | null>(null);

export const useAnswerSectionOverlay = () => {
  const context = useContext(AnswerSectionOverlayContext);
  if (!context) {
    throw new Error('useAnswerSectionOverlay must be used within AnswerSectionOverlayProvider');
  }
  return context;
};

interface AnswerSectionOverlayProviderProps {
  children: ReactNode;
}

export const AnswerSectionOverlayProvider: React.FC<AnswerSectionOverlayProviderProps> = ({ children }) => {
  const [overlayContainer, setOverlayContainer] = useState<HTMLElement | null>(null);
  const [isOverlayActive, setIsOverlayActive] = useState(false);
  const [overlayThreadId, setOverlayThreadId] = useState<string | null>(null);

  const activateOverlay = () => {
    setIsOverlayActive(true);
  };

  const deactivateOverlay = () => {
    setIsOverlayActive(false);
    setOverlayThreadId(null);
    // Don't clear container - it might be reused
  };

  return (
    <AnswerSectionOverlayContext.Provider
      value={{
        overlayContainer,
        setOverlayContainer,
        isOverlayActive,
        activateOverlay,
        deactivateOverlay,
        overlayThreadId,
        setOverlayThreadId,
      }}
    >
      {children}
    </AnswerSectionOverlayContext.Provider>
  );
};
